import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import embLabels from "../../config/emb-labels-en.json";
import ACTIONS from "./Design_Asset_Actions.json";
import isTouchDevice from "../SE-Component/Tooltip";

import { AssetBlockContext } from "../CustomHooks/ContextApi";
import Tooltip from "../../utils/Tooltip";
import { useSelector } from "react-redux";
import useProjectTypeValue from "../../utils/useCurrentProjectValue";

const PVAsset = () => {
  const [isPvGroupNameUsed, setIsPvGroupNameUsed] = useState(false);
  // Capture the initial name so we don't treat it as a duplicate while unchanged
  const initialGroupNameRef = useRef();
  const tooltipData = useSelector((state) => state?.tooltipData?.tooltipData);
  const {
    CBAssetList,
    siteType,
    PVDetails,
    selectedValues,
    CBAssetListDispatch,
    PVDetailsDispatch,
    assetEditStatusUpdatePv,
    updatePvArrayConfig,
    selectedAssetHandlerPv,
    removePvWithAlert,
    removePv,
    isCopyAllButtonDisabled,
    copyAllPvs,
    arrayMaxPowerFinder,
    defaultCbData,
    clientType,
    handleCbAssetRequired,
    handlePmAssetRequired,
    checkAssetStatus,
    spanishCodeRealTime,
    callSnackbar,
    setPrevPvGroupNames,
  } = useContext(AssetBlockContext);

  // Map numeric selection to PVDetails key
  const PV_SET_KEY_MAP = { 3: "pvset1Data", 4: "pvset2Data", 5: "pvset3Data" };

  const currentSetKey = PV_SET_KEY_MAP[selectedValues?.selectedPv];

  const currentConfiguredSetName = useMemo(() => {
    return PVDetails?.[currentSetKey]?.[0]?.groupName || "";
  }, [PVDetails, currentSetKey]);

  // Initialize initial reference once (when first non-empty appears)
  useEffect(() => {
    if (initialGroupNameRef.current === undefined && currentConfiguredSetName) {
      initialGroupNameRef.current = currentConfiguredSetName;
    }
  }, [currentConfiguredSetName]);

  // Gather other set names (first element of each set) excluding current
  const otherSetNames = useMemo(() => {
    const keys = ["pvset1Data", "pvset2Data", "pvset3Data"];
    return keys
      .filter((k) => k !== currentSetKey)
      .map((k) => PVDetails?.[k]?.[0]?.groupName)
      .filter((n) => !!n);
  }, [PVDetails, currentSetKey]);

  // Effective current name from pvConfig (could differ from persisted set name)
  const effectiveCurrentName = useMemo(() => {
    const groupName = PVDetails?.pvConfig?.groupName;
    if (typeof groupName !== "string") return "";
    return groupName.replace(/^[_\s]+$/g, "") || "";
  }, [PVDetails?.pvConfig?.groupName]);

  // Duplicate detection across other sets – exclude original unchanged name of this set
  useEffect(() => {
    if (!effectiveCurrentName) {
      setIsPvGroupNameUsed(false);
      return;
    }
    const duplicate = otherSetNames.includes(effectiveCurrentName);
    setIsPvGroupNameUsed(duplicate);
  }, [effectiveCurrentName, otherSetNames]);
  // Memoized parsing of inverter uiData to avoid repeated JSON.parse in render & handlers
  const inverterUiData = useMemo(() => {
    const safeParseUiData = (uiData) => {
      if (uiData == null) return {};
      if (typeof uiData === "object") return uiData;
      if (typeof uiData !== "string") return {};
      try {
        return JSON.parse(uiData || "{}");
      } catch {
        return {};
      }
    };
    return (
      CBAssetList?.filter((item) => item.assetType === "Inverter")?.map(
        (item) => {
          return safeParseUiData(item?.uiData);
        }
      ) || []
    );
  }, [CBAssetList]);

  // Unique non-empty group names across sets (first occurrence per name)
  const uniqueGroupNames = useMemo(() => {
    const names = [];
    inverterUiData.forEach((d) => {
      if (d.groupName && !names.includes(d.groupName)) names.push(d.groupName);
    });
    return names;
  }, [inverterUiData]);

  // Expose previous PV group names upward whenever the list changes
  useEffect(() => {
    setPrevPvGroupNames(uniqueGroupNames);
  }, [uniqueGroupNames, setPrevPvGroupNames]);

  // Determine if typed name already exists anywhere (excluding empty)
  const groupNameExists = (name) => {
    if (!name) return false;
    // Use otherSetNames instead of every inverter for clearer uniqueness across PV sets
    return otherSetNames.includes(name);
  };

  // Returns true if there is a cross-set duplicate among the three PV sets after applying current edits
  const hasDuplicateAcrossSets = () => {
    const names = [
      PVDetails?.pvset1Data?.[0]?.groupName,
      PVDetails?.pvset2Data?.[0]?.groupName,
      PVDetails?.pvset3Data?.[0]?.groupName,
    ].filter((n) => !!n);
    return new Set(names).size !== names.length;
  };
  const pvArrayChangeHandler = (e) => {
    const { value } = e.target;
    const pvArrayName = value.replace(/^[_\s]+$/g, "");
    setIsPvGroupNameUsed(groupNameExists(pvArrayName));
    // Batch related updates – still using existing action types for compatibility
    PVDetailsDispatch({ type: ACTIONS.CONFIGURE_PV_ARRAY, payload: { data: e.target } });
    PVDetailsDispatch({
      type: ACTIONS.CONFIGURE_PV_GROUP_NAME,
      payload: { data: pvArrayName, selectedPv: selectedValues?.selectedPv },
    });
    CBAssetListDispatch({
      type: ACTIONS.CONFIGURE_UPDATE_DER_PV_ARRAY,
      payload: { data: pvArrayName, selectedPvArray: selectedValues?.selectedPv },
    });
    assetEditStatusUpdatePv(e, true, PVDetails.selectedPv, PVDetails.selectSubPv);
  };

  const onChangePvSubSelectionHandler = (selectSubPv) => {
    PVDetailsDispatch({
      type: ACTIONS.INDIVIDUAL_PV_SELECT,
      payload: {
        selectSubPv: selectSubPv,
        selectedPv: selectedValues.selectedPv,
        // externalId: gsxExternalId?.utilityExternalId
      },
    });
    selectedAssetHandlerPv(selectedValues.selectedPv, selectSubPv);
  };
  const projectTypeValue = useProjectTypeValue();
 
  // Helper to pick current pv set data array based on selected Pv (3,4,5) mapping
  const currentPvSetData = useMemo(() => {
    switch (parseInt(selectedValues.selectedPv)) {
      case 3:
        return PVDetails.pvset1Data || [];
      case 4:
        return PVDetails.pvset2Data || [];
      case 5:
        return PVDetails.pvset3Data || [];
      default:
        return [];
    }
  }, [selectedValues.selectedPv, PVDetails.pvset1Data, PVDetails.pvset2Data, PVDetails.pvset3Data]);
  const NAME_REGEX = /^[a-zA-Z0-9-_ ]+$/;
  const invalidNameChars = !!effectiveCurrentName && !NAME_REGEX.test(effectiveCurrentName);
  const saveDisabled = !effectiveCurrentName || invalidNameChars || isPvGroupNameUsed;
  const canCopyAll = isCopyAllButtonDisabled(PVDetails.selectedPv);
 const savePvSettings = () => {
    if (saveDisabled) {
      callSnackbar(
        "error",
        `Duplicate PV group names detected. Each set must have a unique group name to proceed with configuration.`
      );
      return;
    } else {
      updatePvArrayConfig();
    }
  };
  return (
    <div name="pv" className="col-md-12 p-0">
     { <div className="col-md-12 pt-2 pl-0 se-white-background">
        <div className="col-md-12 p-0 pb-2 se-white-background d-flex justify-content-between">
          <span>
            <se-icon
              color="primary"
              className="se-icon icon_button se-life-green"
              size="medium"
            >
              solar_panel1
            </se-icon>
            <span className="ml-2 bold">photovoltaic</span>
          </span>
          <span className="mt-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={removePv}
            >
              Remove
            </button>
          </span>
        </div>
      </div>}

      <div className=" col-md-12 dropdown-divider"></div>

      <div className="col-md-12">
        <div className="row">
          <label className=" col-md-6 bold mt-1 se-font-14px">
            Configure PV Array
          </label>
          <div className="col-md-6 mt-1 mb-1 text-right">
            <button
              type="button"
              className="btn btn-secondary btn-sm se-btn-secondary"
              disabled={saveDisabled}
              onClick={savePvSettings}
            >
              <se-icon size="nano">action_save</se-icon>
              <span>&nbsp;</span>Save Settings
            </button>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="exampleFormControlInput1">
            Name <span className="se-mandatory">*</span>
          </label>
          <input
            type="text"
            autoComplete="none"
            id="pvGroupNameInput"
            className={"form-control form-control-sm "}
            name="groupName"
            placeholder="Group Name"
            value={effectiveCurrentName}
            onChange={pvArrayChangeHandler}
          />
          <div
            className="invalid-feedback-custom"
            style={!effectiveCurrentName ? {} : { display: "none" }}
          >
            {embLabels.nameRequired}
          </div>
          <div
            className="invalid-feedback-custom"
            style={isPvGroupNameUsed ? {} : { display: "none" }}
          >
            {"Name is already used for another PV Array"}
          </div>
          <div
            className="invalid-feedback-custom"
            style={invalidNameChars ? {} : { display: "none" }}
          >
            {embLabels.notallowedAlphanumericValues}
          </div>
        </div>
        <div className="form-group mb-1">
          <label htmlFor="exampleFormControlInput1">
            Array Max Power (kWp) <span className="se-mandatory">*</span>
          </label>
          <Tooltip
            position="left"
            iconColor="secondary"
            iconSize="small"
            information={tooltipData?.arrayMaxPower?.data}
          />

          <input
            type="text"
            autoComplete="none"
            className={"form-control form-control-sm "}
            id="pvArrayMaxPowerInput"
            placeholder="Array Max Power (kWp)"
            maxLength="5"
            disabled={true}
            name="group_max_power"
            value={Math.abs(PVDetails.pvConfig.group_max_power)}
            // read-only derived value
            readOnly
          />
          <div
            className="invalid-feedback-custom"
            style={
              !(parseInt(PVDetails.pvConfig.group_max_power) >= 0)
                ? {}
                : { display: "none" }
            }
          >
            Array Max Power(kWp) is required.
          </div>
          {/* <div
            className="invalid-feedback-custom"
            style={
              PVDetails.pvConfig.group_max_power === "" ||
              parseInt(PVDetails.pvConfig.group_max_power) <=
                arrayMaxPowerFinder()
                ? { display: "none" }
                : {}
            }
          >
            Array Max Power should not exceed sum of all PV inverter max power.
          </div> */}
        </div>
        {/* Commenting because for future Int in Daas ANSI */}

        {[
          embLabels.GRID_CONNECTED,
          embLabels.GRID_CONNECTED_GSX,
          embLabels.ISLANDABLE,
           embLabels.OFF_GRID,
        ].includes(siteType) && (
          <div className="form-group mb-1">
            {(siteType === embLabels.GRID_CONNECTED ||
             ( siteType === embLabels.OFF_GRID &&  ["MGaaS IEC", "MGaaS ANSI", "MGaaS ETO"].includes(
                  projectTypeValue
                ) )||
              (siteType === embLabels.ISLANDABLE &&
                clientType === embLabels.GENERIC_PROJECT &&
                ["MGaaS IEC", "MGaaS ANSI", "MGaaS ETO"].includes(
                  projectTypeValue
                ))) && (
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input asCompatible"
                  name="pmRequired"
                  id="pmRequiredValidation"
                  onChange={() =>
                    handlePmAssetRequired(selectedValues.selectedPv)
                  }
                  value={
                    CBAssetList?.[selectedValues?.selectedPv]?.uiData
                      ?.pmRequired
                  }
                  checked={
                    CBAssetList?.[selectedValues?.selectedPv]?.uiData
                      ?.pmRequired
                  }
                />
                <label
                  className="custom-control-label line-height-25"
                  htmlFor="pmRequiredValidation"
                >
                  Enable resource energy monitoring
                </label>
                <Tooltip
                  position="left"
                  iconColor="secondary"
                  iconSize="small"
                  information="Once selected, a power meter will be added to this branch to allow accurate metering, or specific sub metering function"
                />
              </div>
            )}
            {(projectTypeValue === "MGaaS IEC" ||
              ((projectTypeValue === "MGaaS ETO") &&
                (siteType === embLabels.GRID_CONNECTED ||
                  siteType === embLabels.ISLANDABLE || siteType === "OFF_GRID"))) && (
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input asCompatible"
                  name="cBRequired"
                  id="cBRequiredValidation"
                  onChange={(e) =>
                    spanishCodeRealTime
                      ? undefined
                      : handleCbAssetRequired(selectedValues.selectedPv, e)
                  }
                  value={
                    CBAssetList[selectedValues?.selectedPv]?.uiData?.cbRequired
                  }
                  checked={
                    CBAssetList[selectedValues?.selectedPv]?.uiData?.cbRequired
                  }
                />
                <label
                  className="custom-control-label line-height-25"
                  htmlFor="cBRequiredValidation"
                >
                  Enable breaker control
                </label>
                <Tooltip
                  position="left"
                  iconColor="secondary"
                  iconSize="small"
                  information="Select this option to allow manual branch operation via EMO-M HMI."
                />
              </div>
            )}
          </div>
        )}
        <div className=" col-md-12 dropdown-divider"></div>

        <div className="row form-group mt-2 mb-0">
          <label className=" col-md-12 bold mt-1 se-font-14px">
            Configure PV inverters{" "}
          </label>
          <div className="col-md-12 mt-1 mb-1 text-right">
            <button
              type="button"
              className="btn btn-secondary btn-sm se-btn-primary"
              disabled={!canCopyAll}
              onClick={copyAllPvs}
            >
              <se-icon size="nano">action_saveas</se-icon>
              <span>&nbsp;</span>Duplicate Configuration
            </button>
          </div>
        </div>
        <div className="form-group mb-1">
          <label
            className={
              parseInt(PVDetails.pvConfiguredCountInCB) ===
              parseInt(PVDetails.pvCountInCB)
                ? "se-life-green"
                : "se-warning"
            }
          >
            <se-icon color="primary" size="nano">
              information_circle
            </se-icon>
            Configured {PVDetails.pvConfiguredCountInCB} PV out of{" "}
            {PVDetails.pvCountInCB} PV{" "}
          </label>
        </div>

        {currentPvSetData.map((data, index) => (
          <div
            className={
              (PVDetails.selectSubPv === index + 1 && PVDetails.displayPvContent
                ? "selected-asset"
                : data.status === false
                ? "unconfigured"
                : "") + " col-md-12  p-0"
            }
            key={data.name}
          >
            <div className="se-chip col-md-11 text-left d-flex justify-content-between">
              <span>
                <se-icon
                  size="small"
                  color="primary"
                  className="icon-small hydrated"
                >
                  solar_panel1
                </se-icon>
                <span
                  data-testid="PvSubSelectionHandler"
                  className="align-top line-height-25 pointer d-inline-block displayPvChipName "
                  onClick={() => onChangePvSubSelectionHandler(index + 1)}
                >
                  {data.name}
                </span>
              </span>
              <span
                className="pointer"
                onClick={() => removePvWithAlert(index + 1)}
              >
                <se-icon size="small" className="icon-small hydrated">
                  notification_error_plain
                </se-icon>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PVAsset;
